import { useState, useCallback } from 'react'
import axios from 'axios'
import { retryWithBackoff, handleAPIError, RateLimitError } from '../utils/apiErrorHandler'

// Helper function to check if job was created after timeout
async function checkRecentJob(textHash, createdAfter, voiceId, authToken) {
  try {
    const response = await axios.post('/v1/tts/jobs/check-recent/', {
      text_hash: textHash,
      created_after: createdAfter,
      voice_id: voiceId
    }, {
      headers: { Authorization: `Bearer ${authToken}` },
      timeout: 10000 // 10 second timeout for check
    })
    return response.data
  } catch (error) {
    console.error('Failed to check recent job:', error)
    return { found: false }
  }
}

// Helper function to hash text using Web Crypto API
async function hashText(text) {
  try {
    const msgUint8 = new TextEncoder().encode(text)
    const hashBuffer = await crypto.subtle.digest('SHA-256', msgUint8)
    const hashArray = Array.from(new Uint8Array(hashBuffer))
    const hashHex = hashArray.map(b => b.toString(16).padStart(2, '0')).join('')
    return hashHex
  } catch (error) {
    // Fallback to simple hash if Web Crypto API is not available
    let hash = 0
    for (let i = 0; i < text.length; i++) {
      const char = text.charCodeAt(i)
      hash = ((hash << 5) - hash) + char
      hash = hash & hash
    }
    return Math.abs(hash).toString(16)
  }
}

export function useTTSSubmit(authToken, globalSettings = {}) {
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [retryInfo, setRetryInfo] = useState(null)
  const [rateLimitInfo, setRateLimitInfo] = useState(null)

  const submitTTS = useCallback(async (data, options = {}) => {
    const {
      endpoint = '/v1/tts/jobs/',
      maxRetries = 3,
      onProgress = null,
      onRetry = null,
      timeout = 30000, // Default 30 second timeout
      checkStatusOnTimeout = true // Whether to check job status if request times out
    } = options

    setIsSubmitting(true)
    setRetryInfo(null)
    setRateLimitInfo(null)

    try {
      // Clean smart quotes and other problematic characters from text
      if (data.text) {
        data.text = data.text
          .replace(/[""]/g, '"')  // Smart double quotes to straight
          .replace(/['']/g, "'")  // Smart single quotes to straight
          .replace(/–/g, '-')     // En-dash to hyphen
          .replace(/—/g, '--')    // Em-dash to double hyphen
          .replace(/…/g, '...')   // Ellipsis to three dots
      }

      // Idempotency key: one per logical submit, reused across this call's
      // internal retries so the backend returns the same job instead of
      // creating a duplicate if a retry fires after the job was created.
      if (!data.idempotency_key) {
        data.idempotency_key = (typeof crypto !== 'undefined' && crypto.randomUUID)
          ? crypto.randomUUID()
          : `${Date.now()}-${Math.random().toString(16).slice(2)}`
      }

      // Store request time and text hash for timeout handling
      const requestStartTime = new Date().toISOString()
      const textHash = await hashText(data.text)
      
      const result = await retryWithBackoff(
        async () => {
          try {
            const requestHeaders = { Authorization: `Bearer ${authToken}` }

            // Add appropriate X-Infer-Path header based on pipeline selection.
            // 'X' = no header (Infer defaults to PRODUCTION_PATH). Any of
            // A/B/C/D pins the request to that specific path service.
            const pipeline = globalSettings?.inferPipeline || 'X'
            if (pipeline === 'X') {
              console.log('[TTS Submit] No pipeline header (X) - using default routing')
            } else if (['A', 'B', 'C', 'D'].includes(pipeline)) {
              requestHeaders[`X-Infer-Path${pipeline}-Service`] = 'true'
              console.log(`[TTS Submit] X-Infer-Path${pipeline}-Service: true`)
            }

            const response = await axios.post(endpoint, data, {
              headers: requestHeaders,
              timeout: timeout
            })

            // Extract rate limit info from headers
            const headers = response.headers
            if (headers['x-ratelimit-remaining']) {
              setRateLimitInfo({
                limit: parseInt(headers['x-ratelimit-limit']) || null,
                remaining: parseInt(headers['x-ratelimit-remaining']) || null,
                reset: parseInt(headers['x-ratelimit-reset']) || null
              })
            }

            return response.data
          } catch (error) {
            // Check if this is a timeout error
            if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
              console.log('TTS request timed out, checking if job was created...')
              
              if (checkStatusOnTimeout) {
                if (onProgress) {
                  onProgress({
                    status: 'timeout',
                    message: 'Request timed out, checking if job was created...'
                  })
                }
                
                // Check if the job was actually created
                const checkResult = await checkRecentJob(
                  textHash,
                  requestStartTime,
                  data.voice_id,
                  authToken
                )
                
                if (checkResult.found) {
                  console.log('Job was created despite timeout:', checkResult.job)
                  // Return the job data as if the request succeeded
                  return checkResult.job
                }
              }
              
              // Job was not found or check was disabled
              const timeoutError = new Error(`Request timed out after ${timeout/1000} seconds. The job may or may not have been created. Please check the History tab.`)
              timeoutError.code = 'TIMEOUT'
              timeoutError.isTimeout = true
              throw timeoutError
            }
            
            // Store original error details for debugging
            console.error('TTS API Error:', error.response?.status, error.response?.data)
            // Parse the error - this will throw a new error
            handleAPIError(error)
          }
        },
        maxRetries,
        1000, // Initial delay 1 second
        30000, // Max delay 30 seconds
        (attempt, delay, error) => {
          // Don't retry timeout errors
          if (error.isTimeout) {
            return false // Stop retrying
          }
          
          // Update retry info
          setRetryInfo({
            attempt,
            maxRetries,
            nextRetryIn: Math.ceil(delay / 1000),
            error: error.message
          })

          // Call custom retry handler
          if (onRetry) {
            onRetry(attempt, delay, error)
          }

          // Update progress
          if (onProgress) {
            onProgress({
              status: 'retrying',
              attempt,
              maxRetries,
              message: `Retrying in ${Math.ceil(delay / 1000)} seconds...`
            })
          }
          
          return true // Continue retrying
        }
      )

      setIsSubmitting(false)
      setRetryInfo(null)
      return { success: true, data: result }
    } catch (error) {
      setIsSubmitting(false)
      
      // Extract rate limit info if available
      if (error instanceof RateLimitError) {
        setRateLimitInfo({
          limit: error.limit,
          remaining: error.remaining,
          reset: error.reset,
          retryAfter: error.retryAfter
        })
      }

      return { success: false, error }
    }
  }, [authToken, globalSettings])

  const clearRateLimitInfo = useCallback(() => {
    setRateLimitInfo(null)
  }, [])

  return {
    submitTTS,
    isSubmitting,
    retryInfo,
    rateLimitInfo,
    clearRateLimitInfo
  }
}